import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, FileText, Loader2, Trash2, Download, Pencil, FileSpreadsheet } from "lucide-react";
import { API } from "@/api/client";

export default function DashboardPage() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createProject = async () => {
    if (!newName.trim()) { toast.error("Informe o nome do projeto."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name: newName.trim(), data: {} });
      toast.success("Projeto criado");
      setDialogOpen(false);
      setNewName("");
      nav(`/projects/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    } finally {
      setCreating(false);
    }
  };

  const removeProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Projeto excluído");
      setProjects((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    }
  };

  const downloadPdf = async (id, name) => {
    try {
      const res = await fetch(`${API}/projects/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Falha ao gerar PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PRONAF_${(name || "projeto").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF gerado");
    } catch (e) {
      toast.error(e.message || "Erro ao baixar PDF");
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground tracking-tight">
              Meus Projetos
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Gerencie, edite e gere PDFs dos projetos PRONAF Grupo A - Investimento.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary" data-testid="create-project-button">
                <Plus className="h-4 w-4 mr-1" /> Novo projeto
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-project-dialog">
              <DialogHeader>
                <DialogTitle>Novo projeto PRONAF</DialogTitle>
                <DialogDescription>
                  Dê um nome ao projeto. Você poderá preencher os 12 passos em seguida.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="project-name">Nome do projeto</Label>
                <Input
                  id="project-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Ex: João da Silva - Assentamento Vitória"
                  data-testid="new-project-name-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-create-project">
                  Cancelar
                </Button>
                <Button onClick={createProject} disabled={creating} data-testid="confirm-create-project">
                  {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Criar projeto
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="border-dashed" data-testid="empty-state">
            <CardContent className="py-20 text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-secondary flex items-center justify-center">
                <FileSpreadsheet className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl font-semibold">Nenhum projeto ainda</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Crie seu primeiro projeto PRONAF Grupo A. Todos os cálculos de financiamento,
                cronograma de reembolso e fluxo de caixa serão feitos automaticamente.
              </p>
              <Button onClick={() => setDialogOpen(true)} data-testid="create-first-project">
                <Plus className="h-4 w-4 mr-1" /> Criar primeiro projeto
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <Card
                key={p.id}
                className="hover:-translate-y-1 hover:shadow-lg transition-all"
                data-testid={`project-card-${p.id}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="font-display text-lg">
                    <div className="flex items-start gap-2">
                      <FileText className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <span className="leading-tight">{p.name}</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground space-y-1 mb-4">
                    {p.applicant_name && (
                      <div>
                        <span className="font-medium text-foreground">Proponente:</span>{" "}
                        {p.applicant_name}
                      </div>
                    )}
                    <div>
                      Atualizado em{" "}
                      {new Date(p.updated_at).toLocaleDateString("pt-BR")}{" "}
                      {new Date(p.updated_at).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => nav(`/projects/${p.id}`)}
                      data-testid={`edit-project-${p.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" /> Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPdf(p.id, p.name)}
                      data-testid={`pdf-project-${p.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-project-${p.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir projeto?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. O projeto "{p.name}" será removido.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => removeProject(p.id)}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid={`confirm-delete-${p.id}`}
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
